import React, { useState, useCallback } from 'react';
import { View, StyleSheet, FlatList, TouchableOpacity, RefreshControl } from 'react-native';
import { Text, Card, ActivityIndicator, Divider, FAB, Searchbar } from 'react-native-paper';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import api from '../../services/api';

const ESTADOS_CONFIG = {
  borrador:         { label: 'Borrador',     color: '#6C757D', icon: 'create-outline',          textColor: '#fff' },
  confirmada:       { label: 'Confirmada',   color: '#007BFF', icon: 'checkmark-circle',         textColor: '#fff' },
  recibida_parcial: { label: 'Parcial',      color: '#FFC107', icon: 'hourglass',                textColor: '#000' },
  recibida:         { label: 'Recibida',     color: '#28A745', icon: 'checkmark-done-circle',    textColor: '#fff' },
  cancelada:        { label: 'Cancelada',    color: '#DC3545', icon: 'close-circle',             textColor: '#fff' },
};

const ComprasScreen = () => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filtro, setFiltro] = useState('todas');
  const [searchQuery, setSearchQuery] = useState('');
  const [resumen, setResumen] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [data, resumenData] = await Promise.all([
        api.getCompras(),
        api.getComprasResumen()
      ]);
      setCompras(data);
      setResumen(resumenData);
    } catch (error) {
      console.error('Error al cargar compras:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      loadData();
    }, [loadData])
  );

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-HN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const filteredCompras = compras.filter(c => {
    const matchEstado = filtro === 'todas' || c.estado === filtro;
    const query = searchQuery.toLowerCase().trim();
    const matchSearch = !query ||
      (c.numero_compra || '').toLowerCase().includes(query) ||
      (c.proveedor_nombre || '').toLowerCase().includes(query) ||
      (c.factura_proveedor || '').toLowerCase().includes(query) ||
      c.id.toString().includes(query);
    return matchEstado && matchSearch;
  });

  const renderItem = ({ item }) => {
    const config = ESTADOS_CONFIG[item.estado] || ESTADOS_CONFIG.borrador;

    return (
      <TouchableOpacity
        onPress={() => navigation.navigate('DetalleCompra', { compraId: item.id })}
      >
        <Card style={styles.card}>
          <Card.Content>
            <View style={styles.header}>
              <View style={styles.info}>
                <View style={styles.idRow}>
                  <Text style={styles.numero}>{item.numero_compra}</Text>
                  <View style={[styles.estadoChip, { backgroundColor: config.color }]}>
                    <Ionicons name={config.icon} size={12} color={config.textColor} />
                    <Text style={[styles.estadoChipText, { color: config.textColor }]}>{config.label}</Text>
                  </View>
                </View>
                <Text style={styles.proveedor}>
                  <Ionicons name="business" size={14} color="#666" /> {item.proveedor_nombre || 'Sin proveedor'}
                </Text>
                <Text style={styles.usuario}>
                  <Ionicons name="person" size={14} color="#888" /> {item.usuario_nombre || 'N/A'}
                </Text>
              </View>
              <View style={styles.right}>
                <Text style={styles.total}>L. {parseFloat(item.total).toFixed(2)}</Text>
                <Text style={styles.items}>{item.total_items || 0} items</Text>
              </View>
            </View>

            <Divider style={styles.divider} />

            <View style={styles.footer}>
              <Text style={styles.fecha}>
                <Ionicons name="calendar" size={12} color="#888" /> {formatFecha(item.fecha)}
              </Text>
              {item.factura_proveedor && (
                <Text style={styles.factura}>
                  <Ionicons name="document" size={12} color="#17A2B8" /> Fact: {item.factura_proveedor}
                </Text>
              )}
              {item.fecha_entrega_esperada && (
                <Text style={styles.entrega}>
                  <Ionicons name="time" size={12} color="#888" /> Entrega: {formatFecha(item.fecha_entrega_esperada)}
                </Text>
              )}
            </View>
          </Card.Content>
        </Card>
      </TouchableOpacity>
    );
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
        <Text style={styles.loadingText}>Cargando compras...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Buscador */}
      <Searchbar
        placeholder="Buscar por OC, proveedor, factura..."
        onChangeText={setSearchQuery}
        value={searchQuery}
        style={styles.searchbar}
      />

      {/* Filtros */}
      <View style={styles.filtrosContainer}>
        {['todas', 'borrador', 'confirmada', 'recibida_parcial', 'recibida', 'cancelada'].map(f => (
          <TouchableOpacity
            key={f}
            style={[styles.filtroChip, filtro === f && styles.filtroActivo]}
            onPress={() => setFiltro(f)}
          >
            <Text style={[styles.filtroText, filtro === f && styles.filtroTextoActivo]}>
              {f === 'todas' ? 'Todas' : ESTADOS_CONFIG[f]?.label || f}
              {' '}({f === 'todas' ? resumen.total || 0 : resumen[f] || 0})
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <FlatList
        data={filteredCompras}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderItem}
        contentContainerStyle={styles.listContainer}
        ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadData(); }} />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name="bag-handle-outline" size={64} color="#ccc" />
            <Text style={styles.emptyText}>No hay ordenes de compra</Text>
          </View>
        }
      />

      <FAB
        icon="plus"
        style={[styles.fab, { bottom: 16 + insets.bottom }]}
        onPress={() => navigation.navigate('NuevaCompra')}
        label="Nueva OC"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 10, color: '#666' },
  searchbar: { margin: 8, marginBottom: 0, elevation: 2 },
  filtrosContainer: { flexDirection: 'row', padding: 8, flexWrap: 'wrap' },
  filtroChip: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16, backgroundColor: '#E9ECEF', marginRight: 6, marginBottom: 4 },
  filtroActivo: { backgroundColor: '#007BFF' },
  filtroText: { fontSize: 11, color: '#666' },
  filtroTextoActivo: { color: '#fff', fontWeight: 'bold' },
  listContainer: { padding: 8, paddingBottom: 80 },
  card: { elevation: 2 },
  header: { flexDirection: 'row', justifyContent: 'space-between' },
  info: { flex: 1, marginRight: 10 },
  idRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' },
  numero: { fontSize: 15, fontWeight: 'bold', color: '#333', marginRight: 8 },
  estadoChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12, gap: 4 },
  estadoChipText: { fontSize: 10, fontWeight: 'bold' },
  proveedor: { fontSize: 13, color: '#666', marginBottom: 2 },
  usuario: { fontSize: 12, color: '#888' },
  right: { alignItems: 'flex-end' },
  total: { fontSize: 16, fontWeight: 'bold', color: '#28a745' },
  items: { fontSize: 12, color: '#888', marginTop: 2 },
  divider: { marginVertical: 8 },
  footer: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' },
  fecha: { fontSize: 12, color: '#888' },
  factura: { fontSize: 12, color: '#17A2B8' },
  entrega: { fontSize: 12, color: '#888' },
  emptyContainer: { justifyContent: 'center', alignItems: 'center', paddingVertical: 50 },
  emptyText: { marginTop: 10, color: '#888', fontSize: 16 },
  fab: { position: 'absolute', right: 16, bottom: 16, backgroundColor: '#007BFF' },
});

export default ComprasScreen;
